function setEavAttribute (attributeCode, value, dtoAttributes, attributes) {
  if (attributes && attributeCode in attributes) {
    const attr = attributes[attributeCode]
    let access = attr.backendType + 's'
    const frontendInput = attr.frontendInput

    if (frontendInput === 'select' || frontendInput === 'multipleselect') {
      access = frontendInput + 's'
    }

    if (access === 'booleans') {
      if (value && typeof value === 'string') {
        value = value?.toLowerCase()
        value = value === 'ja' || value === 'Yes'
      }
    }

    if (access in dtoAttributes) {
      const found = dtoAttributes[access].find(el => el.attributeId === attr.attributeId)
      if (found) {
        found.value = value
      } else {
        dtoAttributes[access].push({
          attributeCode,
          attributeId: attr.attributeId,
          value
        })
      }
    }
  }
}

function getEavAttribute (attributeCode, dtoAttributes, attributes) {
  if (attributes && dtoAttributes) {
    const attr = attributes[attributeCode]
    if (attr) {
      let access = attr.backendType + 's'

      const frontendInput = attr.frontendInput
      if (frontendInput === 'select' || frontendInput === 'multipleselect') {
        access = frontendInput + 's'
      }

      const typed = dtoAttributes[access]

      if (attr && dtoAttributes && typed) {
        const found = typed.find(el => el.attributeId === attr.attributeId)
        if (found) {
          return found.value
        } else {
          return undefined
        }
      }
    }
  }
}

function getOptionLabel (attributeCode, value, attributes) {
  if (attributes && value) {
    const attr = attributes[attributeCode]
    const found = attr.options.find(el => el.optionId === value.optionId)
    if (found) {
      return found.label
    }
  }
}

function prepareDatetimesForUpload (datetimes) {
  const prepared = []

  datetimes.forEach((datetime) => {
    const dt = Object.assign({}, datetime)
    dt.value = new Date(Date.parse(datetime.value)).toISOString()
    prepared.push(dt)
  })
  return prepared
}

// `ints` map to Java Integer and `decimals` to Java Float on the backend. A value
// Jackson cannot read - '120.000', '1e5', anything beyond the Integer range - makes
// it reject the whole request body with 400 "Failed to read HTTP message", so the
// unrelated attributes of that update are lost as well.
const MAX_JAVA_INT = 2147483647

function toUploadNumber (value, integer) {
  if (value === null || value === undefined) {
    return null
  }

  let number = value

  if (typeof number !== 'number') {
    const trimmed = String(number).trim()

    if (trimmed === '') {
      return null
    }

    number = Number(trimmed)
  }

  if (!isFinite(number)) {
    return null
  }

  if (!integer) {
    return number
  }

  const truncated = Math.trunc(number)

  return Math.abs(truncated) > MAX_JAVA_INT ? null : truncated
}

// values come from text inputs, so they arrive as strings - send real numbers and
// drop everything the backend cannot deserialize
function prepareNumbersForUpload (values, integer = false) {
  const prepared = []

  values.forEach((value) => {
    const number = toUploadNumber(value.value, integer)

    if (number !== null) {
      prepared.push(Object.assign({}, value, { value: number }))
    }
  })
  return prepared
}

function getLabel (attributeCode, attributes) {
  if (!attributes) {
    return attributeCode
  }

  if (attributeCode in attributes) {
    return attributes[attributeCode].label
  } else {
    return attributeCode
  }
}

function getTooltip (attributeCode, attributes) {
  if (!attributes) {
    return attributeCode
  }

  if (attributeCode in attributes) {
    return attributes[attributeCode].tooltip
  } else {
    return attributeCode
  }
}

function getOptions (attributeCode, attributes) {
  if (!attributes) {
    return []
  }

  if (attributeCode in attributes) {
    return attributes[attributeCode].options
  } else {
    return []
  }
}

function countRequired (attributes, attributeSetName, attributeGroupName) {
  if (attributes) {
    if (attributeGroupName) {
      return Object.values(attributes).filter(a => a.attributeSetName === attributeSetName &&
        a.attributeGroupName === attributeGroupName &&
        a.required).length
    } else {
      return Object.values(attributes).filter(a => a.attributeSetName === attributeSetName && a.required).length
    }
  } else {
    return 0
  }
}

function countRequiredDone (readyToSubmitForReview, attributeSetName, attributeGroupName) {
  if (readyToSubmitForReview && attributeSetName in readyToSubmitForReview.attributeSets) {
    const attributeSet = readyToSubmitForReview.attributeSets[attributeSetName]

    if (attributeGroupName && attributeGroupName in attributeSet.attributeGroups) {
      return attributeSet.attributeGroups[attributeGroupName].requiredDone
    } else {
      return attributeSet.requiredDone
    }
  } else {
    return 0
  }
}

function requiredPercent (readyToSubmitForReview, attributeSetName, attributeGroupName) {
  if (readyToSubmitForReview && attributeSetName in readyToSubmitForReview.attributeSets) {
    if (attributeGroupName && attributeGroupName in readyToSubmitForReview.attributeSets[attributeSetName].attributeGroups) {
      const attributeGroup = readyToSubmitForReview.attributeSets[attributeSetName].attributeGroups[attributeGroupName]
      return attributeGroup.requiredPercent
    }

    const attributeSet = readyToSubmitForReview.attributeSets[attributeSetName]
    return attributeSet.requiredPercent
  } else {
    return 0
  }
}

function requiredFieldsClass (done, required) {
  if (done < required) {
    return 'red--text '
  } else if (done === required) {
    return 'green--text '
  } else {
    return ''
  }
}

function unsetEavAttribute (attributeCode, dtoAttributes, attributes) {
  if (attributes && attributeCode in attributes) {
    const attr = attributes[attributeCode]
    let access = attr.backendType + 's'
    const frontendInput = attr.frontendInput

    if (frontendInput === 'select' || frontendInput === 'multipleselect') {
      access = frontendInput + 's'
    }

    if (access in dtoAttributes) {
      const foundIndex = dtoAttributes[access].findIndex(el => el.attributeId === attr.attributeId)
      if (foundIndex >= 0) {
        dtoAttributes[access].splice(foundIndex, 1)
      }
    }
  }
}

const eav = {
  getEavAttribute,
  getOptionLabel,
  setEavAttribute,
  unsetEavAttribute,
  prepareDatetimesForUpload,
  prepareNumbersForUpload,

  getLabel,
  getOptions,
  getTooltip,
  countRequired,
  countRequiredDone,
  requiredFieldsClass,
  requiredPercent
}

export default eav
